/**
 * Token source for the web app when running inside the React Native WebView.
 *
 * The mobile app holds the Firebase auth session natively via
 * @react-native-firebase/auth. The WebView has no shared session, so the web
 * app reaches back through the bridge to get a fresh ID token for each
 * authenticated request.
 *
 * RN side: see apps/mobile/src/bridge/bridgeScript.ts (window.__corcaMobile.getIdToken)
 * and apps/mobile/src/hooks/useWebViewBridge.ts (handler for the round-trip).
 */

import { isMobileWebViewUserAgent } from "@/utils/getIsMobile";

const REQUEST_TIMEOUT_MS = 5000;

type MobileBridge = {
  getIdToken?: () => Promise<string | null>;
  downloadFile?: (url: string, filename: string, contentType: string) => void;
};

declare global {
  interface Window {
    __corcaMobile?: MobileBridge;
    ReactNativeWebView?: { postMessage: (message: string) => void };
  }
}

/** Messages the web app pushes to the native host over the WebView bridge. */
export type MobileBridgeMessage = { type: "nav:openDoc"; payload: { docId: string } };

export function isInsideMobileWebView(): boolean {
  return typeof navigator !== "undefined" && isMobileWebViewUserAgent(navigator.userAgent);
}

/** Post a message to the React Native host. No-op outside the mobile WebView. */
export function postMobileBridgeMessage(message: MobileBridgeMessage): void {
  if (typeof window === "undefined") return;
  window.ReactNativeWebView?.postMessage(JSON.stringify(message));
}

export async function getMobileBridgeIdToken(): Promise<string | null> {
  if (typeof window === "undefined") return null;
  const bridge = window.__corcaMobile;
  if (!bridge?.getIdToken) return null;
  try {
    const result = await Promise.race<string | null>([
      bridge.getIdToken(),
      new Promise<null>((resolve) => {
        setTimeout(() => resolve(null), REQUEST_TIMEOUT_MS);
      }),
    ]);
    return result;
  } catch {
    return null;
  }
}
